import { Controller } from '@hotwired/stimulus'

/**
 * Stimulus-контроллер блока свободных кортов на странице клуба.
 *
 * Решает две задачи, и обе — про честность цифр.
 *
 * Переключение дня. Разметка всех дней приходит с сервера сразу: дни
 * переключаются скрытием, а не запросом. Слотов у клуба сотни, но это
 * несколько килобайт разметки, зато переключение мгновенное и работает
 * без сети.
 *
 * Раскрытие длительностей. В таблице одна строка — это ВАРИАНТ брони
 * (корт, старт, длительность), и один старт приходит несколькими строками:
 * 14:00 на 60 минут и то же 14:00 на 120 минут. Показывать их рядом нельзя,
 * пользователь прочтёт это как два свободных корта. Поэтому кнопка — старт,
 * а варианты раскрываются под ней по клику.
 *
 * Без JS блок остаётся рабочим: первый день виден, варианты у стартов
 * развёрнуты (см. hidden в разметке — его снимает как раз этот контроллер).
 *
 * @extends {Controller}
 */
export default class ClubAvailabilityController extends Controller {
  /**
   * Кнопки дней, панели дней, кнопки стартов и панели вариантов.
   *
   * @type {string[]}
   */
  static targets = ['dayTab', 'dayPanel', 'startButton', 'variants']

  /**
   * Классы активной вкладки дня. Держим здесь, а не в разметке: состояние
   * переключается только отсюда.
   *
   * @type {string[]}
   */
  static ACTIVE_TAB_CLASSES = [
    'bg-btn',
    'text-text-on-dark',
    'border-transparent'
  ]

  /**
   * Классы неактивной вкладки дня.
   *
   * @type {string[]}
   */
  static INACTIVE_TAB_CLASSES = [
    'border-divider',
    'hover:border-divider-strong'
  ]

  /**
   * Показывает первый день и сворачивает раскрытые на сервере варианты.
   *
   * @returns {void}
   */
  connect() {
    this.#collapseAllVariants()
    this.selectDay({ params: { index: 0 } })
  }

  /**
   * Переключает видимый день.
   *
   * @param {{params: {index: number}}} event Событие Stimulus с индексом дня.
   * @returns {void}
   */
  selectDay(event) {
    const index = Number(event.params.index)

    this.dayPanelTargets.forEach((panel, panelIndex) => {
      panel.hidden = panelIndex !== index
    })

    this.dayTabTargets.forEach((tab, tabIndex) => {
      const active = tabIndex === index

      tab.setAttribute('aria-selected', String(active))
      tab.classList.toggle('cursor-default', active)
      ClubAvailabilityController.ACTIVE_TAB_CLASSES.forEach((cssClass) => {
        tab.classList.toggle(cssClass, active)
      })
      ClubAvailabilityController.INACTIVE_TAB_CLASSES.forEach((cssClass) => {
        tab.classList.toggle(cssClass, !active)
      })
    })

    // Смена дня не должна оставлять раскрытым старт из предыдущего.
    this.#collapseAllVariants()
  }

  /**
   * Раскрывает или сворачивает варианты длительности у старта.
   *
   * У старта с единственной длительностью раскрывать нечего — такие кнопки
   * ведут сразу на площадку и сюда не попадают (в разметке у них нет action).
   *
   * @param {{params: {startId: string}}} event Событие Stimulus.
   * @returns {void}
   */
  toggleVariants(event) {
    const startId = event.params.startId
    const panel = this.#variantsFor(startId)

    if (!panel) return

    const willOpen = panel.hidden

    this.#collapseAllVariants()

    panel.hidden = !willOpen
    this.#startButtonFor(startId)?.setAttribute(
      'aria-expanded',
      String(willOpen)
    )

    if (willOpen) this.#revealPanel(panel)
  }

  /**
   * Подтягивает раскрытую панель в поле зрения.
   *
   * Панели лежат под всей сеткой времён, а не под нажатой кнопкой: вставлять
   * их внутрь сетки значит ломать её раскладку. На дне с двумя десятками
   * стартов это уводит панель на семь рядов вниз, и человек не видит, что
   * вообще что-то произошло.
   *
   * @param {HTMLElement} panel Раскрытая панель вариантов.
   * @returns {void}
   */
  #revealPanel(panel) {
    const rect = panel.getBoundingClientRect()
    const fullyVisible = rect.top >= 0 && rect.bottom <= window.innerHeight

    if (fullyVisible) return

    panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }

  /**
   * Сворачивает все панели вариантов.
   *
   * @returns {void}
   */
  #collapseAllVariants() {
    this.variantsTargets.forEach((panel) => {
      panel.hidden = true
    })

    this.startButtonTargets.forEach((button) => {
      if (button.hasAttribute('aria-expanded')) {
        button.setAttribute('aria-expanded', 'false')
      }
    })
  }

  /**
   * @param {string} startId Идентификатор старта.
   * @returns {HTMLElement|undefined}
   */
  #variantsFor(startId) {
    return this.variantsTargets.find(
      (panel) => panel.dataset.clubAvailabilityStartIdParam === startId
    )
  }

  /**
   * @param {string} startId Идентификатор старта.
   * @returns {HTMLElement|undefined}
   */
  #startButtonFor(startId) {
    return this.startButtonTargets.find(
      (button) => button.dataset.clubAvailabilityStartIdParam === startId
    )
  }
}
